/**
 * Triggers a browser download for an in-memory blob.
 *
 * The one copy. This nine-line DOM idiom had grown a duplicate in HomePage and AuditLogReport, and
 * the four report exports would have made it four more.
 */
export function downloadFile(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Deferred, not revoked in the same tick as the click: Firefox and Safari can abort a download
  // whose object URL is revoked synchronously.
  window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
}

/**
 * Names an export after the report and the day it was taken, so a folder of them stays sortable.
 *
 * A `blob:` URL has no HTTP response behind it — the bytes are already in memory — so the browser
 * never reads the server's Content-Disposition on this path. This name is authoritative, not a
 * fallback.
 */
export function timestampedFilename(prefix: string): string {
  return `${prefix}-${new Date().toISOString().split('T')[0]}.csv`;
}

/**
 * Names a report export after the period it covers, not the day it was taken.
 *
 * For an accreditation artifact the period is the identifying fact. Stamping the export date
 * instead means three periods pulled in one afternoon land as `qa-coverage-2026-07-29.csv`,
 * `…(1).csv`, `…(2).csv` — a folder in which no file can be told from another without opening it.
 */
export function reportFilename(prefix: string, startDate: string, endDate: string): string {
  if (!startDate && !endDate) return timestampedFilename(prefix);
  return `${prefix}-${startDate || 'start'}_to_${endDate || 'end'}.csv`;
}

/**
 * Reads a server error message out of a failed blob-typed response.
 *
 * Necessary because an error on a `responseType: 'blob'` request carries its body as a Blob, which
 * the api client's interceptor cannot read a `.message` off — every such failure arrives at the
 * caller as the generic string "An error occurred". Without this the server's own explanation of
 * why an export was refused is unreachable, no matter how specific it was.
 */
export async function readBlobErrorMessage(error: unknown): Promise<string | null> {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (!(data instanceof Blob)) return null;

  try {
    const parsed = JSON.parse(await data.text()) as { error?: string };
    return parsed.error ?? null;
  } catch {
    return null;
  }
}
